import { useMemo } from 'react';

import * as Recharts from 'recharts';

import MetricValueFormatter from '../../MetricValueFormatter';
import ActiveShape from '../ActiveShape';
import PieLabel from '../PieLabel';

const sizeMap = {
  sm: {
    outerRadius: '45%',
    labelOffset: 8,
  },
  md: {
    outerRadius: '70%',
    labelOffset: 10,
  },
  lg: {
    outerRadius: '85%',
    labelOffset: 10,
  },
};

export const usePieElements = ({
  metrics,
  colorGetter,
  hiddenMetrics,
  metricSum,
  hoveredMetric,
  onHover,
  size,
  labelType,
}) => {
  const handlePieEnter = ({ id }) => {
    onHover(id);
  };

  const handlePieLeave = () => {
    onHover(null);
  };

  const sizeConfig = sizeMap[size];

  return useMemo(() => {
    const base = [];
    const compared = [];
    let hasComparedMetrics = false;

    metrics.forEach((metric) => {
      if (metric.compared) {
        compared.push(metric);
        hasComparedMetrics = true;
      } else {
        base.push(metric);
      }
    });

    return [base, compared].map((pieMetrics, i) => {
      const data = pieMetrics.map(({ name, dataKey }) => ({
        name,
        value: metricSum[dataKey],
      }));

      const cx = hasComparedMetrics ? (i ? '75%' : '25%') : undefined;
      const sumValue = data.reduce((s, { value = 0 }) => s + value, 0);
      const activeIndex = pieMetrics.map(({ id }) => id).indexOf(hoveredMetric);

      return (
        <Recharts.Pie
          key={i}
          data={data}
          cx={cx}
          dataKey={'value'}
          isAnimationActive={false}
          activeShape={<ActiveShape />}
          activeIndex={activeIndex}
          onMouseEnter={handlePieEnter}
          onMouseLeave={handlePieLeave}
          label={
            <PieLabel
              size={size}
              offset={sizeConfig.labelOffset}
              labelType={labelType}
            />
          }
          labelLine={false}
          outerRadius={sizeConfig.outerRadius}
        >
          {pieMetrics.map(({ colorKey, valueType, id }, i) => {
            const hidden = hiddenMetrics.includes(id);

            const color =
              colorKey !== undefined ? colorGetter(colorKey) : undefined;

            const formatter = <MetricValueFormatter type={valueType} />;
            const percentValue = (data[i]?.value || 0) / sumValue;

            return (
              <Recharts.Cell
                fill={color}
                key={id}
                id={id}
                fillOpacity={hidden ? 0 : 1}
                formatter={formatter}
                percentValue={percentValue}
              />
            );
          })}
        </Recharts.Pie>
      );
    });
  }, [
    hiddenMetrics,
    hoveredMetric,
    labelType,
    metrics,
    colorGetter,
    metricSum,
    onHover,
    size,
  ]);
};
